import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { ReactNode, createContext, memo, useContext, useState } from 'react';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { Card, IconButton, Paragraph, Spinner } from '@treams/treams-ui';

import { AddIcon, ReorderIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { LibraryRenderCard } from './LibraryRenderer';

export const LibraryItemLayoutScope = createContext('item');

const Container = styled(Flex)<{ loading?: boolean; dragging?: boolean; hover?: boolean }>`
	border-radius: ${tokens.radius.md}px;
	transition: 0.2s ease-in-out background-color;

	${(props) =>
		!props.loading &&
		(props.dragging
			? `background: ${tokens.surface.secondary};`
			: props.hover && `background: ${tokens.surface.hover};`)};
`;

const Handle = styled(Flex)<{ loading?: boolean; dragging?: boolean }>`
	${(props) => !props.loading && !props.dragging && `cursor: grab;`}
	width: 40px;
	color: ${tokens.icon.tertiary};

	svg {
		height: 24px;
		width: 24px;
		color: inherit;

		path {
			fill: currentColor;
		}
	}
`;

const PlaceholderContainer = styled(Flex)`
	border: 1px dashed ${tokens.border.default};
	min-height: 64px;
	border-radius: ${tokens.radius.md}px;
	cursor: pointer;
	background: ${tokens.surface.white};
	transition:
		0.2s ease-in-out border-color,
		0.2s ease-in-out background-color;

	&:hover {
		background: ${tokens.surface.hover};

		&:active {
			background: ${tokens.surface.secondary};
		}
	}
`;

type LibraryItemProps<U> = {
	active?: boolean;
	item: U;
	query?: string;
	loading?: boolean;
	selected?: boolean;
	new?: boolean;
	dragging?: boolean;
	draggable?: boolean;
	readOnly?: boolean;
	embeddedHandle?: boolean;
	handle?: object;
	handleIcon?: ReactNode;
	buttons?: ReactNode[];
	onCancel?: () => Promise<void> | void;
	onCreate?: (item: U) => void;
	onUpdate?: (item: U) => void;
	onSelect?: (item: U) => void;
	onForget?: (item: U) => void;
	renderCard: LibraryRenderCard<U>;
};

export function LibraryItem<U extends { id: string }>(props: LibraryItemProps<U>) {
	const loading = props.loading || (props.dragging && !props.active);
	const cardProps = { ...props, loading };

	const [hover, setHover] = useState(false);

	if (props.embeddedHandle) {
		return props.renderCard(cardProps);
	}

	return (
		<Container
			loading={loading}
			dragging={props.dragging}
			hover={hover}
			pl={(props.draggable ?? true) ? 0 : 2}
		>
			{(props.draggable ?? true) && (
				<Handle
					{...(loading ? {} : props.handle)}
					loading={loading}
					dragging={props.dragging}
					flexGrow={0}
					flexShrink={0}
					px={2}
					alignItems="center"
					justifyContent="center"
					onMouseEnter={() => setHover(true)}
					onMouseLeave={() => setHover(false)}
				>
					{loading ? <Spinner /> : (props.handleIcon ?? <ReorderIcon />)}
				</Handle>
			)}
			{props.renderCard(cardProps)}
		</Container>
	);
}

LibraryItem.Memo = memo(LibraryItem) as unknown as typeof LibraryItem;

function LibraryItemDraggable<U extends { id: string }>(props: LibraryItemProps<U>) {
	const { active } = useDndContext();
	const layoutScope = useContext(LibraryItemLayoutScope);
	const sortableId = props.item.id ? `item:${props.item.id}` : '$new';
	const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
		id: sortableId,
		disabled: (active?.id as string)?.startsWith('category:'),
	});

	const categoryDrag = !!active?.id.toString().startsWith('category:');
	const dragging = isDragging || active?.id === sortableId;

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: dragging ? 0 : 1,
				visibility: dragging ? 'hidden' : 'visible',
			}}
			{...attributes}
		>
			<motion.div
				layoutId={!categoryDrag && !dragging ? `${layoutScope}:${props.item.id}` : undefined}
				transition={{ duration: categoryDrag || dragging ? 0 : 0.2, ease: 'circOut' }}
			>
				<LibraryItem.Memo active={!!active} {...props} handle={listeners} />
			</motion.div>
		</div>
	);
}

LibraryItem.Draggable = LibraryItemDraggable;

function LibraryItemDragOverlay<U extends { id: string }>(props: LibraryItemProps<U>) {
	const { active } = useDndContext();

	return <LibraryItem.Memo active={!!active} {...props} />;
}

LibraryItem.DragOverlay = memo(LibraryItemDragOverlay) as unknown as typeof LibraryItemDragOverlay;

function LibraryItemPlaceholder(props: { categoryID: string; onClick?: () => void }) {
	const { active } = useDndContext();
	const { attributes, setNodeRef, transform, isDragging } = useSortable({
		id: `$placeholder:${props.categoryID}`,
		disabled: true,
	});

	const animated = !!active?.id.toString().startsWith('item:');

	return (
		<motion.div
			layoutId={animated ? `$placeholder:${props.categoryID}` : undefined}
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0 : 1,
			}}
			{...attributes}
			transition={{ duration: 0.2, ease: 'circOut' }}
		>
			<PlaceholderContainer alignItems="center" justifyContent="center" onClick={props.onClick}>
				<IconButton type="secondary" icon={<AddIcon />} onClick={props.onClick} />
			</PlaceholderContainer>
		</motion.div>
	);
}

LibraryItem.Placeholder = LibraryItemPlaceholder;

function LibraryItemSkeleton(props: { draggable?: boolean }) {
	return (
		<Card.Skeleton ml={props.draggable === false ? 0 : 40}>
			<Paragraph.Skeleton lines={2} />
		</Card.Skeleton>
	);
}

LibraryItem.Skeleton = LibraryItemSkeleton;
